import { useCallback, useEffect, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import { useToast } from '../../../components/toast/ToastProvider';
import {
  getNearbyEndpoint,
  saveNearbyEndpoint,
  suggestNearbyEndpoint,
  uploadNearbyImageEndpoint,
} from '../services/propertyService';
import {
  NearbyApiPlace,
  NearbyPlaceDraft,
  NearbySavePayload,
} from '../models/nearbyModel';

interface Props {
  propertyId: string;
  center?: { lat: number; lng: number };
}

const PLACES_RADIUS_METERS = 3000;
const isOk = (status: number) => status === 200 || status === 201;
// rs-service envuelve las respuestas en { data }.
const unwrap = (data: any) => data?.data ?? data;

const savedToDraft = (place: NearbyApiPlace): NearbyPlaceDraft => ({
  id: place.id,
  name: place.name,
  address: '',
  category: place.category,
  rating: place.rating,
  lat: place.lat,
  lng: place.lng,
  image: place.image,
  googlePhotoUrl: null,
  status: 'saved',
});

const suggestedToDraft = (place: any, index: number): NearbyPlaceDraft => ({
  id: `suggested-${index}-${Date.now()}`,
  name: place.name,
  address: place.address ?? '',
  category: place.category,
  // Nota provisional de Gemini: se reemplaza por la de Google al buscar el lugar.
  rating: place.rating ?? null,
  lat: null,
  lng: null,
  image: null,
  googlePhotoUrl: null,
  status: 'not_searched',
});

// Places (API clásica, librería `places` del mapa de la página): resuelve un lugar por
// nombre y sesgo a las coordenadas de la propiedad. Da el place_id real, la ubicación,
// la nota y las fotos; el place_id que trae la IA no sirve para esto.
const searchGooglePlace = (
  query: string,
  center: { lat: number; lng: number },
): Promise<any | null> =>
  new Promise((resolve, reject) => {
    const google = (window as any).google;
    if (!google?.maps?.places?.PlacesService) {
      reject(new Error('places-not-loaded'));
      return;
    }
    const service = new google.maps.places.PlacesService(
      document.createElement('div'),
    );
    service.findPlaceFromQuery(
      {
        query,
        fields: ['place_id', 'name', 'geometry', 'rating', 'photos'],
        locationBias: new google.maps.Circle({
          center,
          radius: PLACES_RADIUS_METERS,
        }),
      },
      (results: any[] | null, status: string) => {
        const statuses = google.maps.places.PlacesServiceStatus;
        if (status === statuses.OK && results?.length) {
          resolve(results[0]);
        } else if (status === statuses.ZERO_RESULTS) {
          resolve(null);
        } else {
          reject(new Error(status));
        }
      },
    );
  });

const useNearbyPlaces = ({ propertyId, center }: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const toast = useToast();

  const [places, setPlaces] = useState<NearbyPlaceDraft[]>([]);
  const [advantages, setAdvantages] = useState<string[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);

  const updatePlace = useCallback(
    (index: number, patch: Partial<NearbyPlaceDraft>) => {
      setPlaces((current) =>
        current.map((place, i) => (i === index ? { ...place, ...patch } : place)),
      );
    },
    [],
  );

  const applySaved = (body: any) => {
    setPlaces((body.nearby_places ?? []).map(savedToDraft));
    setAdvantages(body.zone_advantages ?? []);
    setGeneratedAt(body.nearby_generated_at ?? null);
  };

  useEffect(() => {
    const load = async () => {
      const { status, data } = await callEndpoint(
        getNearbyEndpoint(propertyId, token),
      );
      if (isOk(status)) {
        applySaved(unwrap(data));
      }
    };
    load().catch((e) => console.error(e));
  }, [propertyId]);

  const searchPlace = useCallback(
    async (index: number, place: NearbyPlaceDraft) => {
      if (!center) return;
      setBusyIndex(index);
      try {
        const result = await searchGooglePlace(
          `${place.name} ${place.address}`.trim(),
          center,
        );
        if (!result) {
          updatePlace(index, { status: 'not_found' });
          return;
        }
        updatePlace(index, {
          id: result.place_id,
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
          // La nota de Google reemplaza a la provisional de la IA.
          rating: result.rating ?? place.rating,
          googlePhotoUrl: result.photos?.[0]?.getUrl({ maxWidth: 480 }) ?? null,
          status: 'found',
        });
      } catch (error: any) {
        updatePlace(index, { status: 'not_found' });
        toast.error(
          error?.message === 'places-not-loaded'
            ? 'Google Maps aún no termina de cargar, intenta de nuevo.'
            : `Google no respondió (${error?.message}). Revisa que Places esté habilitada para la clave.`,
        );
      } finally {
        setBusyIndex(null);
      }
    },
    [center, updatePlace, toast],
  );

  const suggest = async (force = false) => {
    setIsSuggesting(true);
    try {
      const { status, data } = await callEndpoint(
        suggestNearbyEndpoint(propertyId, force, token),
      );
      if (!isOk(status)) {
        toast.error(
          unwrap(data)?.message ?? 'No se pudieron generar los lugares.',
        );
        return;
      }
      const body = unwrap(data);
      if (body.already_generated) {
        applySaved(body);
        toast.info('Esta propiedad ya tenía lugares generados; se muestran los guardados.');
        return;
      }
      const drafts: NearbyPlaceDraft[] = body.places.map(suggestedToDraft);
      setPlaces(drafts);
      setAdvantages(body.zone_advantages ?? []);
      // Uno por uno, para no disparar varias búsquedas a la vez contra Places.
      for (let index = 0; index < drafts.length; index += 1) {
        await searchPlace(index, drafts[index]);
      }
    } finally {
      setIsSuggesting(false);
    }
  };

  const uploadImage = async (index: number, file: Blob, fileName: string) => {
    setBusyIndex(index);
    try {
      const form = new FormData();
      form.append('image', file, fileName);
      const { status, data } = await callEndpoint(
        uploadNearbyImageEndpoint(propertyId, form, token),
      );
      if (!isOk(status)) {
        toast.error(unwrap(data)?.message ?? 'No se pudo subir la imagen.');
        return;
      }
      updatePlace(index, { image: unwrap(data).image });
    } finally {
      setBusyIndex(null);
    }
  };

  // Copia la foto de Google a nuestro bucket; la URL de Google nunca se guarda.
  // 1) Las URLs de fotos de Google (la del PhotoService de `photo.getUrl()` o la pública
  //    lh3.googleusercontent.com) las descarga rs-service con `source_url`: el navegador
  //    no puede leerlas (CORS).
  // 2) Cualquier otra se intenta desde el navegador y, si falla, se avisa para que el
  //    admin suba una foto a mano.
  // Sube la foto de Google al bucket y devuelve la ruta de la imagen guardada.
  const uploadGooglePhoto = async (place: NearbyPlaceDraft): Promise<string> => {
    const { hostname, pathname } = new URL(place.googlePhotoUrl as string);
    const isPublicGooglePhoto =
      hostname.endsWith('.googleusercontent.com') ||
      hostname.endsWith('.ggpht.com') ||
      (hostname === 'maps.googleapis.com' &&
        pathname === '/maps/api/place/js/PhotoService.GetPhoto');

    const form = new FormData();
    if (isPublicGooglePhoto) {
      form.append('source_url', place.googlePhotoUrl as string);
    } else {
      const response = await fetch(place.googlePhotoUrl as string);
      if (!response.ok) throw new Error(String(response.status));
      form.append('image', await response.blob(), 'place.jpg');
    }
    const { status, data } = await callEndpoint(
      uploadNearbyImageEndpoint(propertyId, form, token),
    );
    if (!isOk(status)) throw new Error(unwrap(data)?.message ?? String(status));
    return unwrap(data).image;
  };

  const copyGooglePhoto = async (index: number, place: NearbyPlaceDraft) => {
    if (!place.googlePhotoUrl) return;
    setBusyIndex(index);
    try {
      updatePlace(index, { image: await uploadGooglePhoto(place) });
    } catch (error) {
      console.error('[nearby] no se pudo copiar la foto de Google', { error });
      const reason = error instanceof Error ? error.message : '';
      toast.warning(
        `No se pudo copiar la foto de Google${reason ? `: ${reason}` : ''}. Puedes subir una a mano.`,
      );
    } finally {
      setBusyIndex(null);
    }
  };

  const addPlace = () =>
    setPlaces((current) => [
      ...current,
      {
        id: `manual-${Date.now()}`,
        name: '',
        address: '',
        category: 'Centro comercial',
        rating: null,
        lat: null,
        lng: null,
        image: null,
        googlePhotoUrl: null,
        status: 'not_searched',
      },
    ]);

  const removePlace = (index: number) =>
    setPlaces((current) => current.filter((_, i) => i !== index));

  const setAdvantage = (index: number, value: string) =>
    setAdvantages((current) => current.map((item, i) => (i === index ? value : item)));
  const addAdvantage = () => setAdvantages((current) => [...current, '']);
  const removeAdvantage = (index: number) =>
    setAdvantages((current) => current.filter((_, i) => i !== index));

  const save = async () => {
    const incomplete = places.find(
      (place) => !place.name.trim() || place.lat === null || place.lng === null,
    );
    if (incomplete) {
      toast.error(
        `"${incomplete.name || 'Un lugar'}" no tiene ubicación. Búscalo en Google o quítalo antes de guardar.`,
      );
      return;
    }
    // Al guardar, los lugares con foto de Google que aún no se copiaron la usan
    // directamente; si no, la foto se perdería al recargar.
    const images = await Promise.all(
      places.map(async (place) => {
        if (place.image || !place.googlePhotoUrl) return place.image;
        try {
          return await uploadGooglePhoto(place);
        } catch (error) {
          console.error('[nearby] no se pudo copiar la foto de Google', { error });
          toast.warning(
            `No se pudo copiar la foto de "${place.name}". Puedes subir una a mano.`,
          );
          return null;
        }
      }),
    );
    const payload: NearbySavePayload = {
      nearby_places: places.map((place, index) => ({
        id: place.id,
        name: place.name.trim(),
        rating: place.rating,
        category: place.category,
        image: images[index],
        lat: place.lat as number,
        lng: place.lng as number,
      })),
      zone_advantages: advantages.map((item) => item.trim()).filter(Boolean),
    };
    const { status, data } = await callEndpoint(
      saveNearbyEndpoint(propertyId, payload, token),
    );
    if (!isOk(status)) {
      toast.error(unwrap(data)?.message ?? 'No se pudo guardar.');
      return;
    }
    applySaved(unwrap(data));
    toast.success('Lugares cercanos guardados.');
  };

  return {
    places,
    advantages,
    generatedAt,
    isLoading,
    isSuggesting,
    busyIndex,
    suggest,
    searchPlace,
    updatePlace,
    uploadImage,
    copyGooglePhoto,
    addPlace,
    removePlace,
    setAdvantage,
    addAdvantage,
    removeAdvantage,
    save,
  };
};

export default useNearbyPlaces;
